import { EVENTS, PLAN_CATALOG } from '@tandemone/types'
import axios from 'axios'
import { Factory, Lightbulb, ShoppingBag } from 'lucide-react'
import { useState } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { FormSheet } from '@/components/ui/form-sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SheetClose } from '@/components/ui/sheet'
import { NoticeBanner } from '@/domains/app-shell'
import { useCreateMyCompany } from '@/domains/companies'
import { reportError, track } from '@/domains/observability'
import { useSetupState } from '@/domains/setup'

/** pull the AppError code from the axios envelope (lives at
 *  response.data.error per apps/api/src/plugins/error-handler.ts). falls
 *  back to 'unknown' for non-axios failures or non-AppError responses. */
function errorCodeFrom(failure: unknown): string {
  if (axios.isAxiosError(failure)) {
    const body = failure.response?.data as { error?: string } | undefined
    if (typeof body?.error === 'string') return body.error
  }
  return 'unknown'
}

type CompanyType = 'supplier' | 'buyer'

/** the Add Company sheet, opened from the sidebar CompanySwitcher. two fields:
 *  name + type. slug is derived server-side. on submit the server creates the
 *  company, switches the active-company cookie to it, and our mutation hook
 *  invalidates every query + reconnects the socket so the rest of the app
 *  picks up the new scope without a reload. */
export function AddCompanySheet({
  open,
  onOpenChange
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState<CompanyType | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const mutation = useCreateMyCompany()
  const { setupState } = useSetupState()

  const rep =
    setupState?.isManufacturingRep &&
    setupState.companyId &&
    setupState.companyName
      ? { companyId: setupState.companyId, companyName: setupState.companyName }
      : (setupState?.entitlements.billedThrough ?? null)
  const billedThrough = type === 'supplier' ? rep : null

  const canSubmit =
    name.trim().length > 0 && type !== null && !mutation.isPending

  function reset() {
    setName('')
    setType(null)
    setErrorMessage(null)
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!canSubmit || !type) return
    setErrorMessage(null)
    track(EVENTS.ADD_COMPANY_SHEET_SUBMITTED, { type })
    try {
      await mutation.mutateAsync({
        name: name.trim(),
        type,
        ...(billedThrough && {
          manufacturingRepCompanyId: billedThrough.companyId
        })
      })
      reset()
      onOpenChange(false)
    } catch (failure) {
      reportError(failure)
      track(EVENTS.ADD_COMPANY_SHEET_FAILED, {
        error_code: errorCodeFrom(failure)
      })
      setErrorMessage(extractErrorMessage(failure))
    }
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={next => {
        if (!next) reset()
        onOpenChange(next)
      }}
      title='Add company'
      description='Spin up another company under your account. You can switch between them from the sidebar.'
      footer={
        <div className='flex w-full items-center justify-end gap-2 px-6 pb-4'>
          <SheetClose asChild>
            <button
              type='button'
              className='rounded-xl px-4 py-2 text-sm font-medium text-ink-soft hover:text-ink'
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
          </SheetClose>
          <button
            type='submit'
            form='add-company-form'
            disabled={!canSubmit}
            className='rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50'
          >
            {mutation.isPending ? 'Creating…' : 'Create company'}
          </button>
        </div>
      }
    >
      <form
        id='add-company-form'
        onSubmit={handleSubmit}
        className='flex flex-col gap-5'
      >
        {billedThrough && (
          <NoticeBanner tone='accent' icon={Lightbulb}>
            Billed through {billedThrough.companyName}. Its{' '}
            {PLAN_CATALOG.supplier_growth.label} plan covers all your suppliers.
          </NoticeBanner>
        )}
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='company-name'>Company name</Label>
          <Input
            id='company-name'
            type='text'
            placeholder='Acme Industrial'
            value={name}
            onChange={event => setName(event.target.value)}
            autoFocus
          />
        </div>

        <div className='flex flex-col gap-1.5'>
          <Label id='company-type-label'>Company type</Label>
          <div
            role='radiogroup'
            aria-labelledby='company-type-label'
            className='flex flex-col gap-2'
          >
            <CompanyTypeOption
              label='Supplier'
              description='Get discovered by buyers'
              icon={Factory}
              selected={type === 'supplier'}
              onSelect={() => setType('supplier')}
            />
            <CompanyTypeOption
              label='Buyer'
              description='Find new suppliers'
              icon={ShoppingBag}
              selected={type === 'buyer'}
              onSelect={() => setType('buyer')}
            />
          </div>
        </div>

        {errorMessage ? (
          <p className='text-sm text-red-600'>{errorMessage}</p>
        ) : null}
      </form>
    </FormSheet>
  )
}

function CompanyTypeOption({
  label,
  description,
  icon: Icon,
  selected,
  onSelect
}: {
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type='button'
      role='radio'
      aria-checked={selected}
      onClick={onSelect}
      className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 text-left transition-colors ${
        selected
          ? 'border-accent bg-accent-light'
          : 'border-border bg-page hover:border-accent/40'
      }`}
    >
      <div
        className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
          selected
            ? 'bg-accent text-white'
            : 'bg-accent-light text-accent dark:text-accent-mid'
        }`}
      >
        <Icon className='size-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='text-[14px] font-medium text-ink'>{label}</p>
        <p className='mt-0.5 text-[12px] text-ink-soft'>{description}</p>
      </div>
      <span
        className={`flex size-4 shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-accent bg-accent' : 'border-border bg-page'
        }`}
      >
        {selected && <span className='size-1.5 rounded-full bg-white' />}
      </span>
    </button>
  )
}
